import { useState, useEffect } from 'react'
import {
  Paper,
  makeStyles,
  Collapse,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Dialog,
  IconButton,
  Divider,
  useTheme,
} from '@material-ui/core'
import RemoveIcon from '@material-ui/icons/Remove'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import ButtonSp from '../ui/ButtonSp'
import TuneIcon from '@material-ui/icons/Tune'
import { useTranslation } from 'next-i18next'

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: 0,
    marginBottom: theme.spacing(1),
    ...theme.homeStyle.boxShadow,
    position: 'sticky',
    top: `calc(${theme.homeStyle.headerHeightDesktop}px + 20px)`,
    marginBottom: theme.spacing(4),
  },
  filterGroupHead: {
    height: 50,
    width: '100%',
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #E0E0E0',
    borderTop: '1px solid #E0E0E0',
    cursor: 'pointer',
  },
  filterGroupCollapse: {
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
    display: 'flex',
    flexDirection: 'column',
  },
  modal: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    paddingTop: theme.spacing(5),
  },
  closeModal: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  filterTitle: {
    textAlign: 'center',
    fontSize: 24,
    marginTop: 0,
    ...theme.headerFont,
  },
  rowSpaceEvenly: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-evenly',
  },
  modalButton: {
    width: '40%',
    height: 50,
    margin: `${theme.spacing(1)}px 0`,
  },
}))

const FilterCard = ({
  filterState,
  setFilterState,
  interFilterState,
  setInterFilterState,
  deliveryType,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const [isMobileModalOpen, setMobileModalOpen] = useState(false)

  const { t } = useTranslation('checkprice')

  const classes = useStyles()

  const checkboxes = {
    price: [
      ['p50down', `${t('less-than')} 50 ${t('baht')}`],
      ['p50to100', `50 ${t('baht')} - 100 ${t('baht')}`],
      ['p101to150', `101 ${t('baht')} - 150 ${t('baht')}`],
      ['p150up', `${t('more-than')} 150 ${t('baht')}`],
    ],
    pickupType: [
      ['pickup', t('pick-up')],
      ['dropoff', t('drop-off')],
    ],
    deliveryTime: [
      ['d1down', `1 ${t('day')}`],
      ['d2to3', `2 - 3 ${t('days')}`],
      ['d4to5', `4 - 5 ${t('days')}`],
      ['d6to7', `6 - 7 ${t('days')}`],
      ['d8to14', `8 - 14 ${t('days')}`],
      ['d15up', `15 ${t('days')} ${t('or-more')}`],
    ],
  }

  const interCheckboxes = {
    price: [
      ['p150to500', `150 ${t('baht')} - 500 ${t('baht')}`],
      ['p501to1000', `501 ${t('baht')} - 1000 ${t('baht')}`],
      ['p1001to2000', `1001 ${t('baht')} - 2000 ${t('baht')}`],
      ['p2000up', `${t('more-than')} 2000 ${t('baht')}`],
    ],
    pickupType: [
      ['pickup', t('pick-up')],
      ['dropoff', t('drop-off')],
    ],
    deliveryTime: [
      ['d1down', `1 ${t('day')}`],
      ['d2to3', `2 - 3 ${t('days')}`],
      ['d4to5', `4 - 5 ${t('days')}`],
      ['d6to7', `6 - 7 ${t('days')}`],
      ['d8to14', `8 - 14 ${t('days')}`],
      ['d15up', `15 ${t('days')} ${t('or-more')}`],
    ],
  }

  const FilterGroup = ({ groupName, children }) => {
    const [isOpen, setOpen] = useState(true)

    return (
      <>
        <div
          className={classes.filterGroupHead}
          onClick={() => setOpen(!isOpen)}
        >
          {groupName}
          {isOpen ? <RemoveIcon /> : <AddIcon />}
        </div>
        <Collapse in={isOpen}>
          <div className={classes.filterGroupCollapse}>{children}</div>
        </Collapse>
      </>
    )
  }

  const handleChange = (e) => {
    if (deliveryType === 'inter') {
      setInterFilterState({
        ...interFilterState,
        [e.target.name]: e.target.checked,
      })
    } else {
      setFilterState({
        ...filterState,
        [e.target.name]: e.target.checked,
      })
    }
  }

  const renderFilters = () => {
    const usedCheckboxes =
      deliveryType === 'inter' ? interCheckboxes : checkboxes
    const usedFilterState =
      deliveryType === 'inter' ? interFilterState : filterState

    return (
      <>
        <FilterGroup groupName={t('prices-range')}>
          {usedCheckboxes.price.map(([name, label]) => (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={usedFilterState[name]}
                  onChange={handleChange}
                  name={name}
                />
              }
              label={label}
              key={name}
            />
          ))}
        </FilterGroup>
        <FilterGroup groupName={t('pickup-location')}>
          {usedCheckboxes.pickupType.map(([name, label]) => (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={usedFilterState[name]}
                  onChange={handleChange}
                  name={name}
                />
              }
              label={label}
              key={name}
            />
          ))}
        </FilterGroup>
        <FilterGroup groupName={t('delivery-time')}>
          {usedCheckboxes.deliveryTime.map(([name, label]) => (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={usedFilterState[name]}
                  onChange={handleChange}
                  name={name}
                />
              }
              label={label}
              key={name}
            />
          ))}
        </FilterGroup>
      </>
    )
  }

  const onModalClose = () => {
    setFilterState({
      p50down: false,
      p50to100: false,
      p101to150: false,
      p150up: false,
      pickup: false,
      dropoff: false,
      d1down: false,
      d2to3: false,
      d4to5: false,
      d6to7: false,
      d8to14: false,
      d15up: false,
    })
    setInterFilterState({
      p150to500: false,
      p501to1000: false,
      p1001to2000: false,
      p2000up: false,
      pickup: false,
      dropoff: false,
      d1down: false,
      d2to3: false,
      d4to5: false,
      d6to7: false,
      d8to14: false,
      d15up: false,
    })
    setMobileModalOpen(false)
  }

  return (
    <>
      {isMobile ? (
        <>
          <ButtonSp
            variant="outlined"
            onClick={() => setMobileModalOpen(true)}
            fullWidth
          >
            <TuneIcon style={{ marginRight: 10 }} />
            {t('filter')}
          </ButtonSp>
          <Dialog
            open={isMobileModalOpen}
            onClose={onModalClose}
            aria-labelledby="modal-filter"
            fullScreen
          >
            <div className={classes.modal}>
              <IconButton className={classes.closeModal} onClick={onModalClose}>
                <CloseIcon />
              </IconButton>
              <h3 className={classes.filterTitle}>{t('filter')}</h3>
              {renderFilters()}
              <Divider style={{ margin: '10px 0 0 0' }} />
              <div className={classes.rowSpaceEvenly}>
                <ButtonSp
                  className={classes.modalButton}
                  onClick={onModalClose}
                  variant="outlined"
                  color="default"
                >
                  {t('clear')}
                </ButtonSp>
                <ButtonSp
                  className={classes.modalButton}
                  onClick={() => setMobileModalOpen(false)}
                >
                  {t('show-results')}
                </ButtonSp>
              </div>
            </div>
          </Dialog>
        </>
      ) : (
        <Paper className={classes.paper}>{renderFilters()}</Paper>
      )}
    </>
  )
}

export default FilterCard
